/**
 * Cache mémoire générique pour les blobs IndexedDB — Phase 2,
 * factorisé en incrément 2.3.
 *
 * Pattern réutilisable pour tout type de blob persistant en IDB :
 *   - photos (préfixe `photo:`, ex. `photo:ph_abc`) — Phase 2.2
 *   - voice messages (`voice:`, ex. `voice:msg_xyz`) — Phase 2.3
 *   - attachments éventuels en Phase 2.5 (`attach:`)
 *
 * `createBlobUrlCache(prefix)` retourne une instance indépendante
 * avec sa propre `Map<id, url>` et son propre set `loading`. Les
 * différentes instances ne se partagent rien — c'est ce qu'on veut
 * pour ne pas mélanger les TTL ou les invalidations.
 *
 * Garanties :
 *   - `setUrl(id, url)` pose l'URL immédiatement. Si une URL existait
 *     déjà, elle est remplacée (l'ancienne n'est PAS révoquée — on
 *     considère que c'est la même donnée). Les data: URLs ne sont
 *     pas révocables de toute façon.
 *   - `getUrlNow(id)` retourne le cache ou déclenche un load IDB
 *     async. Pendant le load, retourne `null`. À la résolution,
 *     `bumpUi()` force un re-render. Pas de re-fetch sur clé absente
 *     (évite la boucle).
 *   - `clearUrl(id)` révoque l'éventuelle blob: URL (libère la
 *     mémoire navigateur) et purge cache + loading. Idempotent.
 *   - `reset()` purge tout — utilisé en cleanup test.
 */
import { getBlobUrl } from '../storage/blobs.js';
import { bumpUi } from './ui.js';

export interface BlobUrlCache {
  setUrl(id: string, url: string): void;
  getUrlNow(id: string): string | null;
  clearUrl(id: string): void;
  /** @internal pour tests. */
  reset(): void;
  /** @internal pour tests. */
  has(id: string): boolean;
}

/**
 * Crée une instance de cache. Le `prefix` est concaténé à l'`id`
 * passé aux méthodes pour former la clé IDB (`<prefix>:<id>`). Par
 * convention `prefix` est sans `:`, on l'ajoute ici.
 */
export function createBlobUrlCache(prefix: string): BlobUrlCache {
  const cache = new Map<string, string>();
  const loading = new Set<string>();

  function idbKey(id: string): string {
    return `${prefix}:${id}`;
  }

  return {
    setUrl(id, url) {
      cache.set(id, url);
    },
    getUrlNow(id) {
      const cached = cache.get(id);
      if (cached) return cached;
      if (loading.has(id)) return null;
      loading.add(id);
      void getBlobUrl(idbKey(id)).then((url) => {
        loading.delete(id);
        if (url) {
          cache.set(id, url);
          bumpUi();
        }
        // Pas de bumpUi sur null : évite une boucle infinie de
        // re-render → re-fetch → null sur une clé morte (ex. blob
        // perdu en migration depuis le legacy `[base64]`).
      });
      return null;
    },
    clearUrl(id) {
      const url = cache.get(id);
      if (url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // Ignoré : déjà révoqué par un autre code, ou impl absente.
        }
      }
      cache.delete(id);
      loading.delete(id);
    },
    reset() {
      cache.forEach((url) => {
        if (url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // ignore
          }
        }
      });
      cache.clear();
      loading.clear();
    },
    has(id) {
      return cache.has(id);
    },
  };
}
